import {
	FloatingActionListMenu
} from "client/Components";
import { contains } from "client/Data/searching";
import { DataSourceSortableState } from "client/Data/sorting";
import {
	AlertManager,
	createLocalDataSource,
	useAlerts,
	useSelection,
	useSortHandler
} from "client/Hooks";
import { getExtensionAndType } from "client_legacy/Documents/fileExtensions";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import {
	Dispatch,
	ReactElement,
	RefObject,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./WebDavOverview.module.less";
import {
	ensureNotNil,
	isNotNilOrEmpty
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	getFolderActions,
	getOpenActions,
	getSelectionActions,
	WebDavDocumentAction,
	WebDavDocumentActionAndOthers,
	WebDavDocumentActionContext,
	ActionHandlers,
	getNewActions
} from "./actions";
import { UserEditorPreference } from "client_legacy/Documents/officeDocumentsOpenPreference";
import { useEditorPreference } from "../useEditorPreference";
import { WebDavTableView, WebDavTableViewHandle } from "./WebDavTableView";
import { WebDavListView, WebDavListViewHandle } from "./WebDavListView";
import { SelectMode } from "client/Misc/commonTypes";
import { FolderNavigationStackItem } from "./useFolderNavigationStack";
import { useCopyDialog } from "../Dialogs/CopyDialog";
import { useMoveDialog } from "../Dialogs/MoveDialog";
import { DocumentStoreType } from "client/Api/Documents/common";
import { useAddBookmarkDialog } from "../Bookmarks/AddBookmarkDialog";
import { FileSystemItemModel, WebDavServerModel } from "client/Api/Documents/WebDav/webDav";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import { WebDavConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { DocumentLayoutKind } from "client/Api/Documents/documents";
import { WebDavGridView } from "./WebDavGridView";
import { WebDavActionsWithContext } from "./WebDavPageContent";

export type GridItem = {
	readonly model: FileSystemItemModel;
	readonly id: string;
	readonly size: number | null;
	readonly nameSorting: string;
	readonly name: string;
	readonly extension: string | null;
	readonly creationDate: Date | null;
	readonly creationDateSorting: Date | null;
	readonly modifiedDateSorting: Date | null;
	readonly modifiedDate: Date | null;
	readonly hasBookmark: boolean;
	readonly kind: DocumentItemType;
	readonly parentId: string;
};

const useDataSource = createLocalDataSource<GridItem>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) => contains(item.name, searchTerm)
		}
	})
	.getHook();

export type WebDavOverviewProps = {
	isMobile: boolean;
	isResponsive: boolean;
	server: WebDavServerModel;
	folder: FolderNavigationStackItem;
	models: readonly FileSystemItemModel[];
	isLoadingModels: boolean;
	onOpenFolder(folder: FileSystemItemModel): void;
	searchTerm: string;
	sorting: DataSourceSortableState<GridItem>;
	setSorting: Dispatch<SetStateAction<DataSourceSortableState<GridItem>>>;
	webDavConfiguration: WebDavConfiguration;
	layoutKind: DocumentLayoutKind;
	onActionsChanged: (value: WebDavActionsWithContext) => (() => void);
	onUploadFolder: () => void;
	onUploadFiles: () => void;
};

export const WebDavOverview = (props: WebDavOverviewProps): ReactElement => {
	const {
		isMobile,
		isResponsive,
		models,
		server,
		folder,
		isLoadingModels,
		onOpenFolder,
		searchTerm,
		sorting,
		webDavConfiguration,
		setSorting,
		layoutKind,
		onActionsChanged,
		onUploadFiles,
		onUploadFolder
	} = props;

	const [selection, setSelection] = useState<readonly GridItem[]>([]);
	const [selectMode, setSelectMode] = useState<SelectMode>(SelectMode.Disabled);
	const tableViewRef: RefObject<WebDavTableViewHandle | null> = useRef<WebDavTableViewHandle>(null);
	const listViewRef: RefObject<WebDavListViewHandle | null> = useRef<WebDavListViewHandle>(null);

	const [copyDialog, openCopyDialog] = useCopyDialog();
	const [moveDialog, openMoveDialog] = useMoveDialog();
	const [addBookmarkDialog, openAddBookmarkDialog] = useAddBookmarkDialog();

	const items = useMemo(
		(): readonly GridItem[] => {
			return models.map(convertToGridItem);
		},
		[models]
	);

	const alertManager: AlertManager = useAlerts();

	const { sortingFor } = useSortHandler<GridItem>(
		sorting,
		setSorting,
		true
	);

	const { currentItems } = useDataSource(
		items,
		{
			searchTerm: searchTerm,
			sorting: sorting
		}
	);

	const { selectionFor, multiSelection } = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems: selection,
			setSelectedItems: setSelection
		}
	);

	const editorPreference: UserEditorPreference = useEditorPreference();

	const copyItemHandler = useCallback(
		(selection: readonly FileSystemItemModel[]) => {
			openCopyDialog({
				thingsToCopy: {
					storeKind: DocumentStoreType.WebDav,
					source: {
						kind: DocumentStoreType.WebDav,
						folder: {
							id: folder.id,
							name: folder.name,
							server: server,
							parentId: null
						}
					},
					documents: selection
				}
			});
		},
		[
			folder.id,
			folder.name,
			openCopyDialog,
			server
		]
	);

	const moveItemHandler = useCallback(
		(selection: readonly FileSystemItemModel[]) => {
			openMoveDialog({
				thingsToMove: {
					storeKind: DocumentStoreType.WebDav,
					source: {
						kind: DocumentStoreType.WebDav,
						folder: {
							id: folder.id,
							name: folder.name,
							server: server,
							parentId: null
						}
					},
					documents: selection
				}
			});
		},
		[
			folder.id,
			folder.name,
			openMoveDialog,
			server
		]
	);
	const actionHandlers = useMemo(
		(): ActionHandlers => {
			return {
				openFolder: onOpenFolder,
				selectItem: (item) => {
					if (isMobile && selectMode === SelectMode.Disabled) {
						return;
					}

					setSelection(

						[
							ensureNotNil(
								currentItems.find(x => x.id === item.id)
							)
						]
					);
				},
				enableSelectMode: () => setSelectMode(SelectMode.Manual),
				disableSelectMode: () => setSelectMode(SelectMode.Disabled),
				selectAll: () => setSelection(currentItems),
				deselectAll: () => setSelection([]),
				onCopyItems: copyItemHandler,
				onMoveItems: moveItemHandler,
				addBookmark: (server, item) => {
					openAddBookmarkDialog({
						store: DocumentStoreType.WebDav,
						serverId: server.id,
						item
					});
				},
				uploadFolder: onUploadFolder,
				uploadFiles: onUploadFiles
			};
		},
		[
			onOpenFolder,
			copyItemHandler,
			moveItemHandler,
			isMobile,
			selectMode,
			currentItems,
			openAddBookmarkDialog,
			onUploadFolder,
			onUploadFiles
		]
	);

	const actionContext = useMemo(
		(): WebDavDocumentActionContext => {
			return {
				server,
				folder: {
					id: folder.id,
					name: folder.name
				},
				selectedDocuments: selection.map(x => x.model),
				isMobile,
				isInSelectMode: selectMode !== SelectMode.Disabled
			};
		},
		[
			folder.id,
			folder.name,
			selection,
			server,
			isMobile,
			selectMode
		]
	);

	const selectionActions = useMemo(
		(): readonly WebDavDocumentAction[] => getSelectionActions(
			actionHandlers,
			currentItems.map(x => x.model),
			alertManager,
			editorPreference,
			webDavConfiguration
		),
		[
			actionHandlers,
			alertManager,
			currentItems,
			editorPreference,
			webDavConfiguration
		]
	);

	const folderActions = useMemo(
		(): readonly WebDavDocumentActionAndOthers[] => {
			return getFolderActions(
				currentItems.map(x => x.model),
				actionHandlers,
				!isResponsive,
				webDavConfiguration
			);
		},
		[
			currentItems,
			actionHandlers,
			isResponsive,
			webDavConfiguration
		]
	);

	const newActions = useMemo(
		(): readonly WebDavDocumentActionAndOthers[] => {
			return getNewActions(webDavConfiguration, false);
		},
		[webDavConfiguration]
	);

	const allActions = useMemo(
		(): readonly WebDavDocumentActionAndOthers[] => [...folderActions, ...selectionActions],
		[folderActions, selectionActions]
	);

	useEffect(
		(): (() => void) => {
			return onActionsChanged({
				context: actionContext,
				actions: allActions
			});
		},
		[
			allActions,
			actionContext,
			onActionsChanged
		]
	);

	useEffect(
		() => {
			hasImplicitDependencyOn(
				folder,
				searchTerm,
				sorting
			);

			tableViewRef.current
				?.resetScrollPosition();

			listViewRef.current
				?.resetScrollPosition();
		},
		[
			folder,
			searchTerm,
			sorting
		]
	);

	useEffect(
		() => {
			if (selectMode === SelectMode.FromLongTouch && selection.length === 0) {
				setSelectMode(SelectMode.Disabled);
			}
		},
		[
			selectMode,
			selection.length,
			setSelectMode
		]
	);

	useEffect(
		(): void => {
			if (selectMode === SelectMode.Disabled
				&& isMobile
			) {
				setSelection([]);
			}
		},
		[selectMode, isMobile]
	);

	const openItemHandler = (item: FileSystemItemModel): void => {
		const context: WebDavDocumentActionContext = {
			...actionContext,
			selectedDocuments: [item]
		};

		const actions: readonly WebDavDocumentAction[] = getOpenActions(
			actionHandlers,
			currentItems.map(x => x.model),
			webDavConfiguration,
			alertManager,
			editorPreference
		);

		actions.filter(x => x.canExecute(context))[0]
			?.execute(context);
	};

	const renderContent = (): ReactElement => {
		if (!isMobile && layoutKind === DocumentLayoutKind.List) {
			return (
				<WebDavTableView
					isBusy={isLoadingModels}
					ref={tableViewRef}
					sortingFor={sortingFor}
					multiSelection={multiSelection}
					selectionFor={selectionFor}
					items={currentItems}
					onOpenItem={openItemHandler}
					selectionActions={selectionActions}
					actionContext={actionContext}
					hasSearch={isNotNilOrEmpty(searchTerm)}
				/>
			);
		}

		if (layoutKind === DocumentLayoutKind.Grid) {
			return (
				<WebDavGridView
					isBusy={isLoadingModels}
					ref={listViewRef}
					selectionFor={selectionFor}
					items={currentItems}
					isInSelectMode={selectMode !== SelectMode.Disabled}
					onEnableSelectMode={() => setSelectMode(SelectMode.FromLongTouch)}
					onOpenItem={openItemHandler}
					selectionActions={selectionActions}
					actionContext={actionContext}
					multiSelection={multiSelection}
					hasSearch={isNotNilOrEmpty(searchTerm)}
				/>
			);
		}

		return (
			<WebDavListView
				isBusy={isLoadingModels}
				ref={listViewRef}
				selectionFor={selectionFor}
				items={currentItems}
				isInSelectMode={selectMode !== SelectMode.Disabled}
				onEnableSelectMode={() => setSelectMode(SelectMode.FromLongTouch)}
				onOpenItem={openItemHandler}
				selectionActions={selectionActions}
				actionContext={actionContext}
				hasSearch={isNotNilOrEmpty(searchTerm)}
			/>
		);
	};

	return (
		<div className={styles.root}>
			<div className={styles.itemsContainer}>
				{renderContent()}
			</div>

			<FloatingActionListMenu
				actions={newActions}
				actionContext={actionContext}
				menuTitle={localize("_Create")}
				trackingId="webdav-documents-fab-menu"
			/>
			{copyDialog}
			{moveDialog}
			{addBookmarkDialog}
		</div>
	);
};
WebDavOverview.displayName = "WebDavOverview";

export function convertToGridItem(model: FileSystemItemModel): GridItem {
	// For sorting we want to group folders and files (i.e. folders first, files second when using asc)
	// Though if we were do to it the "proper" way (i.e. having a special comparer function) the performance
	// , especially with large data sets, degrade enormously. Therefor we use this "hack" of modifying the
	// model "fields" which is then run through the native comparer (mucho fasto)

	// For string values we prefix with 0_ or 1_
	const sortingPrefix: string = model.isFolder ? "0_" : "1_";
	// For sorting Date values we subtract/add an abritrary number of years (should be large enough though
	// to not cause conflicts with "real" dates)
	const sortingYearOffset: number = model.isFolder ? -3000 : 3000;

	const { extension } = getExtensionAndType(model.name, model.isFolder);

	const {
		date: creationDate,
		sorting: creationDateSorting
	} = getGridDate(
		model.creationDate,
		sortingYearOffset
	);

	const {
		date: modifiedDate,
		sorting: modifiedDateSorting
	} = getGridDate(
		model.modifiedDate,
		sortingYearOffset
	);

	const gridItem: GridItem = {
		model,
		id: model.id,
		size: model.size,
		name: model.name,
		nameSorting: sortingPrefix + model.name,
		extension,
		creationDate,
		creationDateSorting,
		modifiedDate,
		modifiedDateSorting,
		hasBookmark: model.bookmarkId !== null,
		kind: model.isFolder ? DocumentItemType.Folder : DocumentItemType.File,
		parentId: model.parentId
	};

	return gridItem;
}

function getGridDate(
	value: string | null,
	sortingYearOffset: number
): { date: Date | null; sorting: Date | null; } {
	if (!value) {
		return {
			date: null,
			sorting: null
		};
	}

	const date: Date = parseDtoDate(value);

	const sorting = new Date(date.getTime());
	sorting.setFullYear(date.getFullYear() + sortingYearOffset);

	return {
		date,
		sorting
	};
}
